import { useEffect, useMemo, useRef, useState } from 'react';
import { Campaign } from '@interfaces';
import {
  findActiveCueIndex,
  fetchTranscript,
  fetchAiTranscript,
  getAiTranscriptUrl,
  TranscriptCue,
} from '@utils/transcript';

export const TRANSCRIPT_SOURCE = {
  ai: 'ai',
  standard: 'standard',
} as const;

export type TranscriptSource = (typeof TRANSCRIPT_SOURCE)[keyof typeof TRANSCRIPT_SOURCE];

interface UseTranscriptReturn {
  lines: TranscriptCue[];
  activeIndex: number;
  isLoading: boolean;
  isGenerating: boolean;
  error: Error | null;
  // Which engine produced the lines currently shown: the AI (Whisper) transcript is
  // the default whenever a completed build exists; 'standard' means Mux captions.
  source: TranscriptSource;
}

const INITIAL_STATE: UseTranscriptReturn = {
  lines: [],
  activeIndex: -1,
  isLoading: false,
  isGenerating: false,
  error: null,
  source: TRANSCRIPT_SOURCE.standard,
};

export const useTranscript = (
  campaign: Campaign | undefined,
  video: HTMLVideoElement | null
): UseTranscriptReturn => {
  const metaVideo = campaign?.meta?.video;
  const muxPlaybackId = metaVideo?.muxPlaybackId;
  const muxCaptions = metaVideo?.muxCaptions;
  const languageCode = metaVideo?.languageCode;

  // Prefer the track matching the asset's selected language; fall back to the
  // first available track when there's no preference or no match.
  const captionTrack = useMemo(() => {
    if (!muxCaptions?.length) return undefined;
    if (languageCode) {
      const match = muxCaptions.find(
        (captions) => captions.language_code === languageCode
      );
      if (match) return match;
    }
    return muxCaptions[0];
  }, [muxCaptions, languageCode]);
  const captionId = captionTrack?.id;
  const aiTranscriptUrl = getAiTranscriptUrl(metaVideo, campaign?.dynamicVideoProps);

  const [lines, setLines] = useState<TranscriptCue[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [source, setSource] = useState<TranscriptSource>(TRANSCRIPT_SOURCE.standard);
  // Source identity of the cues already loaded — when the AI source is active, later
  // Mux changes (captions hydrating, track switches) must not re-download the same JSON.
  const loadedKeyRef = useRef<string | null>(null);

  const hasMuxTrack = !!(muxPlaybackId && captionId);
  const isGenerating = !aiTranscriptUrl && !hasMuxTrack;

  useEffect(() => {
    const hasMux = !!(muxPlaybackId && captionId);
    if (!aiTranscriptUrl && !hasMux) {
      loadedKeyRef.current = null;
      setLines([]);
      setActiveIndex(-1);
      setIsLoading(false);
      setError(null);
      setSource(TRANSCRIPT_SOURCE.standard);
      return undefined;
    }

    const sourceKey = aiTranscriptUrl || `${muxPlaybackId}:${captionId}`;
    if (loadedKeyRef.current === sourceKey) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    // The AI transcript is the default source when available; if its file is
    // unreachable/corrupt we silently fall back to the Mux captions when they exist.
    const load = async (): Promise<{
      cues: TranscriptCue[];
      from: TranscriptSource;
    }> => {
      if (aiTranscriptUrl) {
        try {
          return { cues: await fetchAiTranscript(aiTranscriptUrl), from: TRANSCRIPT_SOURCE.ai };
        } catch (err) {
          if (!hasMux) throw err;
        }
      }
      return {
        cues: await fetchTranscript({
          muxPlaybackId: muxPlaybackId as string,
          captionId: captionId as string,
        }),
        from: TRANSCRIPT_SOURCE.standard,
      };
    };

    load()
      .then(({ cues, from }) => {
        if (cancelled) return;
        loadedKeyRef.current =
          from === TRANSCRIPT_SOURCE.ai
            ? (aiTranscriptUrl as string)
            : `${muxPlaybackId as string}:${captionId as string}`;
        setLines(cues);
        setSource(from);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLines([]);
        setSource(TRANSCRIPT_SOURCE.standard);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [muxPlaybackId, captionId, aiTranscriptUrl]);

  useEffect(() => {
    if (!video || lines.length === 0) {
      setActiveIndex(-1);
      return undefined;
    }

    const updateActive = () => {
      setActiveIndex((prev) => {
        const next = findActiveCueIndex(lines, video.currentTime);
        return next === prev ? prev : next;
      });
    };

    updateActive();
    video.addEventListener('timeupdate', updateActive);
    video.addEventListener('seeked', updateActive);

    return () => {
      video.removeEventListener('timeupdate', updateActive);
      video.removeEventListener('seeked', updateActive);
    };
  }, [lines, video]);

  if (isGenerating) {
    return { ...INITIAL_STATE, isGenerating: true };
  }

  return { lines, activeIndex, isLoading, isGenerating: false, error, source };
};

export default useTranscript;
